import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Button } from "@/components/ui";
import { useT } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { cn } from "@/lib/cn";
import {
  useWorkToastStore,
  type WorkToastItem,
} from "@/stores/work-toast-store";
import { toastShellAttributes, toastShellClassName } from "./toast-pill";
import { WorkToastLogo, WorkToastPill } from "./work-toast-pill";
import type { useDraggableTop } from "./use-draggable-top";

type DragHandlers = ReturnType<typeof useDraggableTop>;

/**
 * Right-edge rail for ordinary subagent results. Computer-use toasts live
 * beneath the tray menu icon.
 */
export function SubagentToastRail({
  pillItems,
  hasItems,
  displayId,
  expandedKey,
  drag,
}: {
  pillItems: WorkToastItem[];
  hasItems: boolean;
  displayId: number | null;
  expandedKey: string | null;
  drag: Pick<DragHandlers, "gripHandlers" | "takeDragged">;
}) {
  const t = useT(toastsMessages);
  const [revealed, setRevealed] = useState(false);
  const hideTimer = useRef<number | null>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const menuOpenKeyRef = useRef<string | null>(null);
  const pillItemsRef = useRef(pillItems);
  pillItemsRef.current = pillItems;
  const focusRevealKey = useRef<string | null>(null);
  // Entrance plays once per task (first sight of its key), never on
  // hover-reveal: hovering only slides the already-mounted pills out.
  const seenKeys = useRef<Set<string>>(new Set());

  useEffect(
    () => () => {
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    },
    [],
  );

  useLayoutEffect(() => {
    const key = focusRevealKey.current;
    if (!revealed || !key) return;
    focusRevealKey.current = null;
    const item = Array.from(
      railRef.current?.querySelectorAll<HTMLElement>("[data-rail-item-key]") ??
        [],
    ).find((node) => node.dataset.railItemKey === key);
    item
      ?.querySelector<HTMLElement>('[data-testid="toast-pill-body"]')
      ?.focus();
  }, [revealed]);

  const show = useCallback(() => {
    if (hideTimer.current !== null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    setRevealed(true);
  }, []);

  // Delayed hide: the cursor drifting slightly off the cards, crossing
  // gaps, or layout shifts from newly arriving pills must not snap the rail
  // shut mid-hover. Generous grace; blur still hides immediately. Hiding
  // also folds an expanded rail card back to its pill, so collapse always
  // returns to the same icon slivers.
  const scheduleHide = useCallback(() => {
    if (menuOpenKeyRef.current) return;
    if (hideTimer.current !== null) return;
    const keys = new Set(pillItemsRef.current.map((item) => item.key));
    hideTimer.current = window.setTimeout(() => {
      hideTimer.current = null;
      if (menuOpenKeyRef.current) return;
      setRevealed(false);
      const store = useWorkToastStore.getState();
      if (store.expandedKey && keys.has(store.expandedKey)) store.collapse();
    }, 800);
  }, []);

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    const next = event.relatedTarget;
    if (
      menuOpenKeyRef.current ||
      (next instanceof Element && next.closest('[data-toast-menu="true"]'))
    )
      return;
    if (!event.currentTarget.contains(next as Node | null)) {
      if (hideTimer.current !== null) {
        window.clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      setRevealed(false);
    }
  }

  const handleMenuOpenChange = useCallback(
    (key: string, open: boolean) => {
      if (!open && menuOpenKeyRef.current !== key) return;
      menuOpenKeyRef.current = open ? key : null;
      if (open) {
        show();
      } else if (
        !railRef.current?.matches(":hover") &&
        !railRef.current?.contains(document.activeElement)
      ) {
        scheduleHide();
      }
    },
    [scheduleHide, show],
  );

  useEffect(() => {
    const key = menuOpenKeyRef.current;
    if (!key || pillItems.some((item) => item.key === key)) return;
    menuOpenKeyRef.current = null;
    if (
      !railRef.current?.matches(":hover") &&
      !railRef.current?.contains(document.activeElement)
    )
      setRevealed(false);
  }, [pillItems]);

  // A drag that moved past the threshold must not also fire the pill's
  // click (which would expand/open right after moving the column).
  function suppressDragClick(event: ReactMouseEvent<HTMLDivElement>) {
    if (drag.takeDragged()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  if (!hasItems) return null;

  return (
    <div
      ref={railRef}
      data-testid="subagent-rail"
      onPointerEnter={show}
      onFocus={(event) => {
        const badge = (event.target as HTMLElement).closest<HTMLElement>(
          "[data-rail-badge-key]",
        );
        if (badge) focusRevealKey.current = badge.dataset.railBadgeKey ?? null;
        show();
      }}
      onBlur={handleBlur}
      onPointerLeave={scheduleHide}
      className="flex flex-col items-start gap-2 self-end overflow-hidden py-1"
    >
      {pillItems.map((item, index) => {
        let animateIn = false;
        if (!seenKeys.current.has(item.key)) {
          seenKeys.current.add(item.key);
          if (seenKeys.current.size > 50) {
            const oldest = seenKeys.current.values().next().value;
            if (oldest !== undefined) seenKeys.current.delete(oldest);
          }
          animateIn = true;
        }
        return (
          <div
            key={item.key}
            data-rail-item-key={item.key}
            className={cn(
              "nodrag pointer-events-auto relative shrink-0 cursor-grab active:cursor-grabbing",
              animateIn && "animate-toast-pill-in",
            )}
            style={
              animateIn
                ? { animationDelay: `${Math.min(index, 5) * 45}ms` }
                : undefined
            }
            {...drag.gripHandlers}
            onClickCapture={suppressDragClick}
          >
            <div className={cn(!revealed && "hidden")}>
              <WorkToastPill
                item={item}
                expanded={expandedKey === item.key}
                displayId={displayId}
                onMenuOpenChange={handleMenuOpenChange}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t("showAgentActivity", { name: item.agentName })}
              className={cn(
                toastShellClassName,
                "grid h-[46px] w-[46px] place-items-center rounded-full",
                revealed && "hidden",
              )}
              data-testid={`subagent-rail-badge-${item.key}`}
              data-rail-badge-key={item.key}
              data-interactive="true"
              {...toastShellAttributes}
            >
              <WorkToastLogo item={item} />
            </Button>
          </div>
        );
      })}
      {pillItems.length === 0 && hasItems && (
        <p className="w-[46px] px-1 py-2 text-appkit-small text-muted">…</p>
      )}
    </div>
  );
}
